import type { LegalTemplate } from '@/mocks/superAdminLegalTemplates';
import { getActiveVersion } from '@/mocks/superAdminLegalTemplates';

interface TemplateCardProps {
  template: LegalTemplate;
  onPreview: (t: LegalTemplate) => void;
  onEdit: (t: LegalTemplate) => void;
  onHistory: (t: LegalTemplate) => void;
  onDuplicate: (t: LegalTemplate) => void;
  onActivate: (t: LegalTemplate) => void;
}

export default function TemplateCard({
  template,
  onPreview,
  onEdit,
  onHistory,
  onDuplicate,
  onActivate,
}: TemplateCardProps) {
  const activeVersion = getActiveVersion(template);
  const hasDraft = template.versions.some((v) => v.status === 'draft');
  const versionCount = template.versions.length;
  const previewSnippet = activeVersion.content.slice(0, 120).replace(/\n/g, ' ') + '...';

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden hover:border-foreground-300 transition-all duration-200 group">
      {/* Top accent bar */}
      <div className={`h-1.5 w-full ${template.isActive ? 'bg-emerald-400' : 'bg-stone-300'}`}></div>

      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center shrink-0">
              <i className={`${template.typeIcon} text-foreground-500 text-base`}></i>
            </div>
            <div className="min-w-0">
              <p className="text-foreground-800 text-xs font-semibold truncate">{template.typeLabel}</p>
              <p className="text-foreground-400 text-[10px] flex items-center gap-1.5">
                <span className="flex items-center gap-1">
                  <i className="ri-git-branch-line text-[9px]"></i>
                  v{template.currentVersion}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>{versionCount} {versionCount === 1 ? 'versión' : 'versiones'}</span>
              </p>
            </div>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold border ${
              template.isActive
                ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                : 'bg-stone-50 text-stone-500 border-stone-200'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${template.isActive ? 'bg-emerald-500' : 'bg-stone-400'}`}></span>
            {template.isActive ? 'Activo' : 'Inactivo'}
          </span>
        </div>

        {/* Preview snippet */}
        <div className="bg-background-100 rounded-xl p-3 border border-background-200/70">
          <p className="text-foreground-500 text-[10px] leading-relaxed line-clamp-3">
            {previewSnippet}
          </p>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between text-[10px] text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-time-line text-[9px]"></i>
            {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line text-[9px]"></i>
            {activeVersion.createdBy.split(' ')[0]}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 pt-1 border-t border-background-100">
          <button
            onClick={() => onPreview(template)}
            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line text-[10px]"></i>
            Vista Previa
          </button>
          <button
            onClick={() => onEdit(template)}
            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-[10px]"></i>
          </button>
          <button
            onClick={() => onHistory(template)}
            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[10px] font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-history-line text-[10px]"></i>
          </button>
          <div className="relative group/more">
            <button className="flex items-center justify-center w-7 h-7 rounded-lg bg-background-100 text-foreground-500 hover:bg-background-200 transition-colors cursor-pointer">
              <i className="ri-more-2-fill text-xs"></i>
            </button>
            <div className="absolute right-0 top-full mt-1 w-44 bg-background-50 border border-background-200 rounded-xl shadow-lg z-20 opacity-0 invisible group-hover/more:opacity-100 group-hover/more:visible transition-all duration-150 py-1">
              {hasDraft && (
                <button
                  onClick={() => onActivate(template)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer text-left whitespace-nowrap"
                >
                  <i className="ri-check-line text-[10px]"></i>
                  Activar versión borrador
                </button>
              )}
              <button
                onClick={() => onDuplicate(template)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer text-left whitespace-nowrap"
              >
                <i className="ri-file-copy-line text-[10px]"></i>
                Duplicar plantilla
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}